<script setup>
import { computed, ref, watch, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  minuteStep: { type: Number, default: 5 },
  // Open time "HH:mm" — start of the allowed window (empty = no constraint).
  minTime: { type: String, default: '' },
  // Close time "HH:mm" — end of the allowed window (empty = no constraint).
  maxTime: { type: String, default: '' },
  // When true, times outside [minTime, maxTime] stay selectable (after-hours).
  allowOutside: { type: Boolean, default: false },
  // Station closed that day with no after-hours — disable everything.
  closed: { type: Boolean, default: false },
  // Overrides the default chrome of the trigger button so the picker can sit
  // seamlessly inside a segmented hero-search cell (borderless variant).
  triggerClass: {
    type: [String, Object, Array],
    default:
      'h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 hover:border-n-slate-6 focus:border-n-brand',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const isOpen = ref(false);
const rootRef = ref(null);
const hourListRef = ref(null);
const minuteListRef = ref(null);

const pad = value => String(value).padStart(2, '0');

const hours = computed(() => Array.from({ length: 24 }, (_, i) => pad(i)));

const minutes = computed(() => {
  const step = props.minuteStep > 0 ? props.minuteStep : 5;
  const list = [];
  for (let m = 0; m < 60; m += step) list.push(pad(m));
  return list;
});

const selectedHour = computed(() => props.modelValue.split(':')[0] || '');
const selectedMinute = computed(() => props.modelValue.split(':')[1] || '');

const triggerLabel = computed(() => props.modelValue || '');

// Minutes since midnight for an "HH:mm" string; null when unparseable/empty.
const toMinutes = value => {
  if (!value) return null;
  const [h, m] = value.split(':');
  return Number(h) * 60 + Number(m);
};

// Whether the given hour/minute is selectable under the schedule constraints.
const isAllowed = (hour, minute) => {
  if (props.closed) return false;
  if (props.allowOutside) return true;
  const min = toMinutes(props.minTime);
  const max = toMinutes(props.maxTime);
  if (min === null || max === null) return true;
  const value = Number(hour) * 60 + Number(minute);
  return value >= min && value <= max;
};

// An hour is selectable when at least one minute step within it is allowed.
const isHourAllowed = hour =>
  minutes.value.some(minute => isAllowed(hour, minute));

const emitTime = (hour, minute) => {
  if (!isAllowed(hour, minute)) return;
  emit('update:modelValue', `${pad(hour)}:${pad(minute)}`);
};

const selectHour = hour => {
  if (!isHourAllowed(hour)) return;
  // Keep the current minute if it stays valid for the new hour; otherwise snap
  // to the first allowed minute step so an allowed hour always selects a time.
  const current = selectedMinute.value || '00';
  const minute = isAllowed(hour, current)
    ? current
    : minutes.value.find(m => isAllowed(hour, m));
  emitTime(hour, minute);
};
const selectMinute = minute => emitTime(selectedHour.value || '00', minute);

const scrollSelectedIntoView = () => {
  [hourListRef.value, minuteListRef.value].forEach(list => {
    const active = list?.querySelector('[data-selected="true"]');
    if (active) active.scrollIntoView({ block: 'center' });
  });
};

const open = () => {
  isOpen.value = true;
  nextTick(scrollSelectedIntoView);
};

const close = () => {
  isOpen.value = false;
};

const toggle = () => (isOpen.value ? close() : open());

const onDocumentClick = event => {
  if (rootRef.value && !rootRef.value.contains(event.target)) close();
};

const onKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) close();
};

watch(isOpen, value => {
  if (value) {
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('click', onDocumentClick, true);
    document.removeEventListener('keydown', onKeydown);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick, true);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      :disabled="closed"
      class="flex items-center gap-2 text-sm text-n-slate-12 outline-none transition-colors"
      :class="[triggerClass, { 'opacity-40 cursor-not-allowed': closed }]"
      @click="toggle"
    >
      <span class="i-lucide-clock text-base text-n-slate-11" />
      <span v-if="triggerLabel" class="truncate">{{ triggerLabel }}</span>
      <span v-else class="truncate text-n-slate-10">
        {{ placeholder || t('CONVERSATION.CAR_BOOKING.TRIP.TIME_PLACEHOLDER') }}
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute top-full right-0 z-50 mt-1 flex w-[160px] rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
    >
      <div class="flex flex-1 flex-col">
        <span class="py-1 text-center text-xs font-medium text-n-slate-10">
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.HOUR') }}
        </span>
        <div ref="hourListRef" class="max-h-48 overflow-y-auto">
          <button
            v-for="hour in hours"
            :key="hour"
            type="button"
            :disabled="!isHourAllowed(hour)"
            :data-selected="hour === selectedHour"
            class="w-full rounded-md px-3 py-1.5 text-center text-sm transition-colors"
            :class="
              !isHourAllowed(hour)
                ? 'opacity-40 cursor-not-allowed text-n-slate-12'
                : hour === selectedHour
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="selectHour(hour)"
          >
            {{ hour }}
          </button>
        </div>
      </div>

      <div class="mx-1 w-px bg-n-weak" />

      <div class="flex flex-1 flex-col">
        <span class="py-1 text-center text-xs font-medium text-n-slate-10">
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.MINUTE') }}
        </span>
        <div ref="minuteListRef" class="max-h-48 overflow-y-auto">
          <button
            v-for="minute in minutes"
            :key="minute"
            type="button"
            :disabled="!isAllowed(selectedHour || '00', minute)"
            :data-selected="minute === selectedMinute"
            class="w-full rounded-md px-3 py-1.5 text-center text-sm transition-colors"
            :class="
              !isAllowed(selectedHour || '00', minute)
                ? 'opacity-40 cursor-not-allowed text-n-slate-12'
                : minute === selectedMinute
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="selectMinute(minute)"
          >
            {{ minute }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
